import { Component, OnInit, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CheckoutPaymentModel } from '../models/payment.model';
import { NgForm } from '@angular/forms';
import { CheckoutService, CheckoutPaymentTypeInterface } from '../checkout.service';
import { AuthService } from '../../auth/auth.service';
import { UserModel } from '../../auth/models/user.model';
import { UIRouter } from '@uirouter/core';
import { CheckoutBasketModel } from '../models/basket.model';

import { CONFIG as APP_CONFIG } from '../../config/app.config';
import { CONFIG as PAYPAL_CONFIG } from '../../config/paypal.config';
import { AppState } from '../../app.service';
import { BehaviorSubject } from 'rxjs';

import { EmbeddedFonts } from '../../global/embedded-fonts';

declare var paypal: any;

@Component({
  templateUrl: './payment.component.html',
  styleUrls: [
    './payment.component.scss'
  ]
})
export class CheckoutPaymentComponent implements OnInit, AfterViewInit {
  private acceptedTermsChangeSubject = new BehaviorSubject<boolean>(null);

  public isStripeTokenLoading: boolean = false;
  public stripeToken: any;
  private stripePaymentMethodId: string;

  public isLoading: boolean = false;

  public payment: CheckoutPaymentModel;
  public basket: CheckoutBasketModel;

  public user: UserModel;

  public checkoutErrorCode: string;

  public cardNumber: stripe.elements.Element;
  public cardExpiration: stripe.elements.Element;
  public cardCvc: stripe.elements.Element;

  public isCardNumberValid: boolean = false;
  public isCardExpirationValid: boolean = false;
  public isCardCvcValid: boolean = false;

  public isCardNumberEmpty: boolean = true;
  public isCardExpirationEmpty: boolean = true;
  public isCardCvcEmpty: boolean = true;

  public checkoutError: string;
  public showNewsletterCheckbox: boolean = false;

  public deactivateStripe: boolean = false;

  @ViewChild('cardNumberElement')
  public cardNumberElement: ElementRef;

  @ViewChild('cardExpirationElement')
  public cardExpirationElement: ElementRef;

  @ViewChild('cardCvcElement')
  public cardCvcElement: ElementRef;

  @ViewChild('checkoutPaymentForm')
  public checkoutPaymentFormElement: NgForm;

  constructor(
    private checkoutService: CheckoutService,
    private authService: AuthService,
    private appState: AppState,
    private router: UIRouter,
    private elementRef: ElementRef
  ) {
  }

  private handleCheckoutErr(err: any) {
    this.checkoutErrorCode = err.code;

    switch (err.key) {
      case 'api_error':
        this.checkoutError = 'Während ihrer Anfrage ist ein Fehler aufgetreten. ' +
          'Bitte versuchen Sie es in einigen Sekunden erneut.';
        break;
      case 'incorrect_number':
        this.checkoutError = 'Die von Ihnen eingegebene Kreditkartennummer ist falsch.';
        break;
      case 'invalid_number':
        this.checkoutError = '';
        break;
      case 'invalid_expiry_month':
        this.checkoutError = 'Der eingegebene Ablaufmonat ist falsch.';
        break;
      case 'invalid_expiry_year':
        this.checkoutError = 'Das eingegebene Ablaufjahr ist falsch.';
        break;
      case 'invalid_cvc':
      case 'incorrect_cvc':
        this.checkoutError = 'Die von Ihnen eingegebene Kreditkarte CVC ist nicht korrekt.';
        break;
      case 'card_declined':
        this.checkoutError = 'Ihre Kreditkarte wurde abgelehnt. ' +
          'Bitte überprüfen Sie die eingegebenen Daten oder kontaktieren Sie Ihre Bank.';
        break;
      case 'processing_error':
        this.checkoutError = 'Beim Verarbeiten Ihrer Kreditkarte ist ein Fehler aufgetreten. ' +
          'Bitte versuchen Sie es in einigen Sekunden erneut.';
        break;
      case 'expired_card':
        this.checkoutError = 'Ihre Kreditkarte ist abgelaufen. Bitte verwenden Sie eine andere Karte.';
        break;
      case 'unknown':
      default:
        this.checkoutError = 'Beim Kauf ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
        break;
    }

    this.isLoading = false;
  }

  private handleCompletedTransaction() {
    console.log('transaction succeeded');

    const params = {
      success: true,
      isNewUser: false,
      wasLoggedIn: true,
      buttonCount: this.checkoutService.basket.countInvoiceableItems +
        this.checkoutService.basket.countNonInvoiceableItems,
      onlyGifts: false
    };

    this.isLoading = false;

    const isOnlyVoucherButtons = this.checkoutService.basket.countNonInvoiceableItems > 0 &&
      this.checkoutService.basket.countInvoiceableItems === 0;
      
    const onlyGifts = this.checkoutService.basket.countGiftItems > 0 && 
      this.checkoutService.basket.countGiftItems === this.checkoutService.basket.countInvoiceableItems;

    this.checkoutService.reset();

    if (isOnlyVoucherButtons) {
      this.router.stateService.go('main.checkout.successVouchers', params);
    } else {
      if (onlyGifts) {
        params.onlyGifts = true;  
      }
      
      this.router.stateService.go('main.checkout.success', params);
    }
  }

  private finalizeCheckout(payment: CheckoutPaymentTypeInterface) {
    if (this.authService.currentUser) {
      console.log('user logged in, continuing');

      this.checkoutError = null;
      this.checkoutService.user.newsletter = this.payment.newsletter ? this.payment.newsletter.toString() : null;

      this.authService.updateCurrentUserAttributes(this.checkoutService.user)
        .subscribe(() => {
          this.authService.getCurrentUser();
        },
        (err) => {
          console.log(err);
        });

      this.checkoutService.checkout(payment)
        .subscribe(
          (result) => {
            if (payment.type === 'stripe') {
              if (typeof result.result !== 'undefined') {
                const stripeResult = result.result;

                if (typeof stripeResult.requires_action  !== 'undefined') {
                  if (stripeResult.requires_action === true) {
                    this.checkoutService.handleStripeCardAction(stripeResult.payment_intent_client_secret)
                      .subscribe((cardActionResult) => {
                        payment.stripe.paymentIntent = cardActionResult.paymentIntent.id;

                        this.checkoutService.checkout(payment)
                          .subscribe(
                            (result) => {
                              this.handleCompletedTransaction();
                            },
                            (err) => {
                              if (typeof err.decline_code !== 'undefined') {
                                if (err.decline_code === 'insufficient_funds') {
                                  this.checkoutError = 'Ihre Karte hat nicht genügend Guthaben.';
                                  this.isLoading = false;
                                }
                              } else {
                                this.handleCheckoutErr(err);
                              }
                            }
                          );
                      },
                      (err) => {
                        if (err.type === 'invalid_request_error') {
                          this.checkoutError = err.message;
                        } else {
                          this.checkoutError = 'Beim Kauf ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.';
                        }

                        this.checkoutService.createStripePaymentMethod(this.cardNumber)
                          .subscribe(
                            (paymentMethodId) => {
                              this.stripePaymentMethodId = paymentMethodId;
                              this.isLoading = false;
                            },
                            (err) => {
                              this.checkoutError = 'Beim Verarbeiten Ihrer Kreditkarte ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
                              this.isLoading = false;
                            }
                          )
                      });

                    return;
                  }
                }
              }
            }

            this.handleCompletedTransaction();
          },
          (err) => {
            this.handleCheckoutErr(err);
          }
        );
    } else {
      console.log('user not logged in, registering');

      this.checkoutService.user.newsletter = this.payment.newsletter ? this.payment.newsletter.toString() : null;

      this.authService.register(this.checkoutService.user)
        .subscribe(
          (registerResult) => {
            console.log('checkout register success');

            this.checkoutService.checkout(payment)
              .subscribe(
                (result) => {
                  console.log('transaction succeeded');

                  let params = {
                    success: true,
                    isNewUser: true,
                    wasLoggedIn: false,
                    buttonCount: this.checkoutService.basket.countInvoiceableItems +
                      this.checkoutService.basket.countNonInvoiceableItems,
                    onlyGifts: false
                  };

                  this.isLoading = false;

                  const isOnlyVoucherButtons = this.checkoutService.basket.countNonInvoiceableItems > 0 &&
                    this.checkoutService.basket.countInvoiceableItems === 0;
                    
                  const onlyGifts = this.checkoutService.basket.countGiftItems > 0 && 
                    this.checkoutService.basket.countGiftItems === this.checkoutService.basket.countInvoiceableItems;

                  this.checkoutService.reset();

                  if (isOnlyVoucherButtons) {
                    this.router.stateService.go('main.checkout.successVouchers', params);
                  } else {
                    if (onlyGifts) {
                      params.onlyGifts = true;  
                    }
                    
                    this.router.stateService.go('main.checkout.success', params);
                  }
                },
                (err) => {
                  console.log('checkout error');
                  console.log(err);

                  this.checkoutErrorCode = err.code;
                  this.isLoading = false;
                }
              );
          },
          (err) => {
            console.log('register error');
            console.log(err);

            this.isLoading = false;

            if (err.code === 'UsernameExistsException') {

              console.log('email exists');

              this.checkoutError = 'Diese E-Mail existiert bereits, ' +
                'bitte loggen Sie sich ein oder setzen Sie Ihr Passwort zurück.';
            } else {
              this.checkoutError = 'Beim Anmelden ist ein Problem aufgetreten. ' +
                'Bitte überprüfen Sie das Formular und versuchen Sie es erneut.';
            }
          }
        );
    }
  }

  private isCardDataValid(): boolean {
    return this.isCardCvcValid && this.isCardExpirationValid && this.isCardNumberValid;
  }

  private resetStripeToken() {
    this.stripeToken = null;

    this.checkoutService.basket.paymentMethod = 'card';
    this.checkoutService.basket.country = null;

    this.checkoutService.calculateTotals();
  }

  private createStripeToken() {
    if (this.isCardDataValid()) {
      this.isStripeTokenLoading = true;

      this.checkoutService.createStripeToken(this.cardNumber, this.payment.cardName)
        .subscribe(
          (tokenResult) => {
            console.log('stripe token result');

            this.isStripeTokenLoading = false;

            this.stripeToken = tokenResult;

            this.checkoutService.basket.paymentMethod = this.payment.method;
            this.checkoutService.basket.country = tokenResult.card.country;

            this.checkoutService.calculateTotals();
          },
          (err) => {
            this.isStripeTokenLoading = false;

            this.isLoading = false;

            switch (err.type) {
              case 'card_error':
                this.checkoutError = 'Ihre Kreditkarte wurde abgelehnt. ' +
                  'Bitte überprüfen Sie die eingegebenen Daten oder kontaktieren Sie Ihre Bank.';

                break;
              default:
                this.checkoutError = 'Beim Verarbeiten Ihrer Kreditkarte ist ein Fehler aufgetreten. ' +
                  'Bitte versuchen Sie es in einigen Sekunden erneut.';
            }

            console.log('stripe token error');
            console.log(err);
          }
        );

      this.checkoutService.createStripePaymentMethod(this.cardNumber)
          .subscribe(
            (paymentMethodId) => {
              this.stripePaymentMethodId = paymentMethodId;
            },
            (err) => {
              this.checkoutError = 'Beim Verarbeiten Ihrer Kreditkarte ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.';
            }
          )
    }
  }

  private createStripeInputs() {
    const elements = this.checkoutService.stripeInstance.elements({
      fonts: [{
        family: 'BlockTReg',
        src: EmbeddedFonts['BlockT-Regu']
      }],
      locale: 'de-DE'
    });

    const inputStyles = {
      fontFamily: 'BlockTReg',
      fontSize: '16px',
      textAlign: 'center',
      letterSpacing: '1.6px',
      textTransform: 'uppercase',
      lineHeight: '1',
      color: '#111111'
    };

    const placeholderStyles = {
      ...inputStyles,
      color: '#E6E6E6'
    };

    this.cardNumber = elements.create('cardNumber', {
      style: {
        base: {
          ...inputStyles,
          '::placeholder': placeholderStyles
        },
        invalid: {
          ...inputStyles
        }
      }
    });
    this.cardNumber.mount(this.cardNumberElement.nativeElement);

    this.cardNumber.on('change', (event) => {
      console.log(event);

      this.isCardNumberValid = !event.error && event.complete;
      this.isCardNumberEmpty = !!event.empty;

      this.checkoutError = null;

      this.resetStripeToken();
      this.createStripeToken();
    });

    this.cardExpiration = elements.create('cardExpiry', {
      style: {
        base: {
          ...inputStyles,
          '::placeholder': placeholderStyles
        },
        invalid: {
          ...inputStyles
        }
      }
    });
    this.cardExpiration.mount(this.cardExpirationElement.nativeElement);

    this.cardExpiration.on('change', (event) => {
      this.isCardExpirationValid = !event.error && event.complete;
      this.isCardExpirationEmpty = !!event.empty;

      this.checkoutError = null;

      this.resetStripeToken();
      this.createStripeToken();
    });

    this.cardCvc = elements.create('cardCvc', {
      style: {
        base: {
          ...inputStyles,
          '::placeholder': placeholderStyles
        },
        invalid: {
          ...inputStyles
        }
      }
    });
    this.cardCvc.mount(this.cardCvcElement.nativeElement);

    this.cardCvc.on('change', (event) => {
      this.isCardCvcValid = !event.error && event.complete;
      this.isCardCvcEmpty = !!event.empty;

      this.checkoutError = null;

      this.resetStripeToken();
      this.createStripeToken();
    });
  }

  private createPaypalButton() {
    paypal.Button.render({
      env: this.appState.env === 'production' ? 'production' : 'sandbox',
      client: {
        sandbox: PAYPAL_CONFIG.credentials.staging.clientId,
        production: PAYPAL_CONFIG.credentials.production.clientId
      },
      style: {
        size: 'responsive',
        shape: 'rect',
        color: 'blue',
        tagline: false
      },
      validate: (actions) => {
        this.acceptedTermsChangeSubject.subscribe(
          (result) => {
            if (result !== null) {
              result ? actions.enable() : actions.disable();
            }
          }
        );

        this.payment.acceptedTerms ? actions.enable() : actions.disable();
      },
      onClick: () => {
        this.checkoutPaymentFormElement.onSubmit(new Event('submit'));
      },
      payment: (data, actions) => {
        this.isLoading = true;

        return actions.payment.create({
          transactions: [
            {
              amount: {
                total: this.basket.grossTotal,
                currency: 'EUR'
              }
            }
          ]
        });
      },
      onAuthorize: (data) => {
        if (data.paymentID && data.payerID) {
          this.finalizeCheckout({
            type: 'paypal',
            paypal: {
              paymentId: data.paymentID,
              payerId: data.payerID
            }
          });
        } else {
          this.isLoading = false;

          this.checkoutError = 'Bei der Verarbeitung Ihrer PayPal-Transaktion ist ein ' +
            'Fehler aufgetreten. Bitte versuchen Sie es in einigen Sekunden erneut.';
        }
      },
      onError: (err) => {
        console.log(err);

        this.isLoading = false;

        this.checkoutError = 'Bei der Verarbeitung Ihrer PayPal-Transaktion ist ein ' +
          'Fehler aufgetreten. Bitte versuchen Sie es in einigen Sekunden erneut.';
      },
      commit: true,
      locale: 'de_DE',
      onCancel: (data) => {
        this.isLoading = false;
        console.log('The payment was cancelled!');
      }
    }, '#paypal_container');
  }

  public ngOnInit() {
    if (
      (!this.checkoutService.items || !this.checkoutService.items.length) ||
      !this.checkoutService.user
    ) {
      this.router.stateService.go('main.checkout.index');
    }
    
    if (typeof this.checkoutService.user !== 'undefined' && this.checkoutService.user !== null) {
      if (this.checkoutService.user.newsletter === '1') {
        this.showNewsletterCheckbox = false;
      } else {
        this.showNewsletterCheckbox = false;
      }
    } else {
      this.showNewsletterCheckbox = false;
    }

    this.basket = this.checkoutService.basket;

    this.authService.currentUserChange.subscribe(
      (result) => {
        if (!result) {
          this.router.stateService.go('main.checkout.index');
        }
      }
    );

    this.payment = new CheckoutPaymentModel();

    if (this.checkoutService.basket.countInvoiceableItems === 0) {
      this.payment.method = 'none';
    }

    if (this.deactivateStripe) {
      this.payment.method = 'paypal';
    }
  }

  public ngAfterViewInit() {
    if (this.checkoutService.basket.countInvoiceableItems > 0) {
      if (!this.deactivateStripe) {
        this.createStripeInputs();
      }

      this.createPaypalButton();
    }
  }

  public handleAcceptedTermsChange() {
    this.acceptedTermsChangeSubject.next(this.payment.acceptedTerms);
  }

  public handleMethodClick() {
    this.checkoutPaymentFormElement.reset();

    this.payment = new CheckoutPaymentModel();
  }

  public handleMethodChange() {
    this.checkoutError = null;

    this.checkoutService.setPayment(this.payment);

    this.checkoutService.basket.paymentMethod = this.payment.method;

    this.checkoutService.calculateTotals();
  }

  public handleSubmit(form: NgForm) {
    if (form.valid && !this.isLoading) {
      if (this.payment.method === 'card' && this.isCardDataValid() && this.stripeToken) {
        this.isLoading = true;

        this.finalizeCheckout({
          type: 'stripe',
          stripe: {
            tokenId: this.stripePaymentMethodId,
            country: this.stripeToken.card.country
          }
        });
      } else if (this.payment.method === 'none') {
        this.isLoading = true;

        this.finalizeCheckout({
          type: 'none'
        });
      }
    } else if (!form.valid && !this.isLoading) {
      let firstInvalidControl = this.elementRef.nativeElement.querySelectorAll('.ng-invalid');
      
      if (firstInvalidControl.length > 0) {
        if (typeof firstInvalidControl[1] !== 'undefined' && firstInvalidControl[1] !== null) {
          firstInvalidControl[1].scrollIntoView(true);
        } else {
          firstInvalidControl[0].scrollIntoView(true);
        }
      }
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/checkout/payment/payment.component.ts